import React, { useMemo } from "react";

export type DateSelectorProps = {
  selectedDate: Date;
  onChange: (d: Date) => void;
};

export const DateSelector: React.FC<DateSelectorProps> = ({
  selectedDate,
  onChange,
}) => {
  // Anchor: today at local midnight
  const today = useMemo(() => {
    const now = new Date();
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0, 0, 0, 0
    );
  }, []);

  // Build fixed window: tomorrow, today, past 5 days
  const options = useMemo(() => {
    const days: Date[] = [];

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    days.push(tomorrow);

    days.push(new Date(today));

    for (let i = 1; i <= 5; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      days.push(d);
    }

    return days;
  }, [today]);

  const minDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(today.getDate() - 5);
    return d;
  }, [today]);

  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(today.getDate() + 1);
    return d;
  }, [today]);

  const clampAndChange = (d: Date) => {
    if (d < minDate) return;
    if (d > maxDate) return;
    onChange(d);
  };

  const formatLabel = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;

  return (
    <div className="flex justify-center items-center gap-3 my-4">
      {/* Back */}
      <button
        disabled={selectedDate <= minDate}
        onClick={() => {
          const prev = new Date(selectedDate);
          prev.setDate(selectedDate.getDate() - 1);
          clampAndChange(prev);
        }}
        className="
          px-3 py-2 rounded-md border text-sm font-medium
          disabled:opacity-40 disabled:cursor-not-allowed
          hover:bg-gray-100
        "
      >
        ‹
      </button>

      {/* Date select */}
      <select
        value={selectedDate.toISOString()}
        onChange={(e) => onChange(new Date(e.target.value))}
        className="
          min-w-[170px] px-3 py-2 text-sm text-center
          border border-gray-300 rounded-md
          bg-white cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-blue-500
        "
      >
        {options.map((d) => {
          const iso = d.toISOString();
          const isSelected = iso === selectedDate.toISOString();

          return (
            <option
              key={iso}
              value={iso}
              className={isSelected ? "font-semibold" : ""}
            >
              {formatLabel(d)}
            </option>
          );
        })}
      </select>

      {/* Forward */}
      <button
        disabled={selectedDate >= maxDate}
        onClick={() => {
          const next = new Date(selectedDate);
          next.setDate(selectedDate.getDate() + 1);
          clampAndChange(next);
        }}
        className="
          px-3 py-2 rounded-md border text-sm font-medium
          disabled:opacity-40 disabled:cursor-not-allowed
          hover:bg-gray-100
        "
      >
        ›
      </button>
    </div>
  );
};
